import { IBaseAlertProps } from "@components/alert/Alert.types";
import { BreadCrumbProvider } from "@components/breadCrumb";
import Header from "@components/header/Header";
import { ISplitPageWizardTask, SplitPageWizard } from "@components/splitWizard/SplitPageWizard";
import { ITabData, ITabsProps } from "@components/tabs";
import { ODataError } from "@odata/Data.types";
import { IPrYearOperationEntity, IPrYearOperationSectionEntity } from "@odata/GeneratedEntityTypes";
import { PrYearOperationSectionTypeCode } from "@odata/GeneratedEnums";
import {
    getFormForSectionType,
    SortedPRYearOperationSections
} from "@pages/payroll/payrollYearOperations/PayrollYearOperation.utils";
import { logger } from "@utils/log";
import dayjs from "dayjs";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Status } from "../../../enums";
import { getAlertFromError } from "../../../views/formView/Form.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import View from "../../../views/View";

interface IProps {
    year: string;
    task?: string;
    storage: FormStorage;
    fileViewRef: React.RefObject<HTMLDivElement>;
    onTaskSelect?: (taskId: string) => void;
    tabsDefinition?: ITabsProps;
    hasProcessedPeriods?: boolean;
}

const PayrollYearOperationView: React.FC<IProps> = ({
                                                        year,
                                                        task,
                                                        storage,
                                                        fileViewRef,
                                                        onTaskSelect,
                                                        tabsDefinition,
                                                        hasProcessedPeriods
                                                    }) => {
    const { t } = useTranslation(["PayrollYearOperation", "Common"]);
    const entity = storage.data.entity as IPrYearOperationEntity;

    useEffect(() => {
        storage.context.setViewBreadcrumbs({
            lockable: false,
            items: [{
                key: "prYearOperation",
                title: `${t(`PayrollYearOperation:SectionType.${task}`)}`
            }]
        });
    }, [task, t, storage.context]);

    const handleTaskSelect = useCallback((taskId: string) => {
        onTaskSelect?.(taskId);
    }, [onTaskSelect]);

    const handleTaskCheck = useCallback(async (checked: boolean): Promise<null | IBaseAlertProps> => {
        try {
            const sections = entity?.Sections.map(s => {
                if (s.TypeCode === task) {
                    return {
                        ...s,
                        IsChecked: checked
                    };
                }
                return s;
            });

            const res = await storage.save({
                data: {
                    Sections: sections
                }
            });

            if (!res) {
                // todo better error handling, but for now storage returns null in case of error so we can show a generic error
                return {
                    status: Status.Error,
                    title: t("Common:Errors.ErrorHappened")
                };
            }
            return null;
        } catch (e) {
            logger.error(`Error while checking/unchecking payroll year operation section (${task})`, e);
            return getAlertFromError(e as ODataError);
        }
    }, [storage, entity.Sections, task, t]);

    const nextYear = parseInt(year) + 1;

    function isDisabled(section: PrYearOperationSectionTypeCode): boolean {
        switch (section) {
            case PrYearOperationSectionTypeCode.PayrollSheetsProcessing:
                return !hasProcessedPeriods;
            case PrYearOperationSectionTypeCode.TaxpayerDeclaration:
                return dayjs().isBefore(dayjs().set("year", parseInt(year) + 1).startOf("year"));
            default:
                return false;
        }
    }

    const tasks = storage.data.entity?.Sections?.map((section: IPrYearOperationSectionEntity) => ({
        id: section.TypeCode,
        title: t(`PayrollYearOperation:SectionType.${section.TypeCode}`, { year, nextYear }),
        isChecked: section.IsChecked,
        isSelected: section.TypeCode === task && !isDisabled(section.TypeCode),
        isDisabled: isDisabled(section.TypeCode),
        isStep: false,
    }));

    const sortedTasks = tasks?.sort((a: ITabData, b: ITabData) => {
        return SortedPRYearOperationSections.indexOf(a.id as PrYearOperationSectionTypeCode) - SortedPRYearOperationSections.indexOf(b.id as PrYearOperationSectionTypeCode);
    }) as ISplitPageWizardTask[];

    let selectedTaskId = task;
    if (sortedTasks.find((t) => t.id === task)?.isDisabled) {
        const firstEnabledTask = sortedTasks.find((t) => !t.isDisabled);
        firstEnabledTask.isSelected = true;
        selectedTaskId = firstEnabledTask.id;
    }
    const selectedTask = storage.data.entity?.Sections?.find((section: IPrYearOperationSectionEntity) => section.TypeCode === selectedTaskId);


    const renderForm = useCallback(() => {
        const title = t(`PayrollYearOperation:SectionType.${selectedTaskId}`, { year, nextYear });
        const SectionForm = getFormForSectionType(selectedTaskId as PrYearOperationSectionTypeCode);

        return <>
            <BreadCrumbProvider/>
            <View hotspotContextId={storage.data.definition.id}>
                <Header title={title}/>
                <SectionForm
                        task={selectedTask}
                        onCheck={handleTaskCheck}
                        storage={storage}
                        year={year}/>
            </View>
        </>;
    }, [task, t, selectedTask, handleTaskCheck, storage, year]);

    return (
            <SplitPageWizard
                    storage={storage}
                    fileViewRef={fileViewRef}
                    selectedTaskId={selectedTaskId}
                    onTaskSelect={handleTaskSelect}
                    hasFiles={!!entity?.Attachments?.length}
                    listTitle={t("PayrollYearOperation:ListTitle")}
                    tasks={sortedTasks}
                    tabsDefinition={tabsDefinition}
                    renderForm={renderForm}
            />
    );
};

export default PayrollYearOperationView;